import { useEffect, useRef, useState } from 'react';
import {
  api,
  ShoulderTapAnalysis,
  ShoulderTapItem,
  ShoulderTapPayload,
} from '../services/api';
import { useMetrics } from './useMetrics';
import { METRICS_EVENTS } from '../constants';
import {
  setWorkerTimeout,
  clearWorkerTimeout,
  setWorkerInterval,
  clearWorkerInterval,
  isUsingWorkerTimers,
} from '../utils/workerTimer';
import { breadcrumb } from '../utils/diagnostics';

const DEFAULT_POLL_INTERVAL_SECONDS = 15;
const OFFLINE_FAILURE_THRESHOLD = 3;

// Heartbeat metrics ride the chat WebSocket, so we sample rather than emit on
// every poll. At a 3s cadence this is roughly one event per minute — frequent
// enough to bound when a frame went quiet, cheap enough to leave on always.
const HEARTBEAT_METRIC_EVERY_N_POLLS = 20;

// Backstop watchdog. Even with the per-request timeout in the API layer,
// we want a hard guarantee that the poll loop is always ticking: if no new
// poll has *started* within WATCHDOG_INTERVAL_MULTIPLIER × the poll
// interval, we assume the loop wedged (an unexpected throw, a suspended
// timer, an await that never settled) and force-restart it. The multiplier
// is comfortably larger than the 5s request timeout, so a merely slow (but
// alive) loop never trips it.
const WATCHDOG_INTERVAL_MULTIPLIER = 3;

// Retry cadence for `/shoulder-tap-configuration` when the service
// responds `enabled=false`. The server's decision depends on a
// per-contact eligibility latch written by the Kinesis Lambda on the
// first RTCA segment — if the UI mounts before the customer/agent
// has sent a message that produces an RTCA segment (and before
// Connect has routed the contact to a queue), that latch doesn't
// exist yet and the endpoint fails-closed. We retry a small number
// of times to catch the flip. If still disabled at ~100s post-mount
// something else is wrong (queue not on allowlist, kill switch off,
// etc.) and no amount of polling will fix it — give up.
const CONFIG_RETRY_DELAYS_MS = [10_000, 30_000, 60_000];

// Every poll parses new JSON, so the two arrays are never the same object even
// when the sequence hasn't changed. Comparing ids is what lets us hand back the
// old array and skip a re-render.
const sameTaps = (a: ShoulderTapItem[], b: ShoulderTapItem[]): boolean =>
  a.length === b.length && a.every((tap, i) => tap.id === b[i].id);

// The server always sends `taps`, but a rolled-back or lagging stage might not.
// Guarded here because `sameTaps` on a non-array throws from inside a state
// updater, and React runs those during render — the poll loop's try/catch
// wouldn't catch it, so the whole tree would go down. Missing means no taps,
// which resolves to the base script.
const tapsFrom = (result: ShoulderTapAnalysis): ShoulderTapItem[] =>
  Array.isArray(result.taps) ? result.taps : [];

export interface ShoulderTapAnalysisState {
  tap: ShoulderTapPayload | null;
  /**
   * Every tap in the contact's active lifecycle (all taps since the last
   * dismiss), oldest first — present on every poll, not just surfacing ones.
   * This is what copy must be resolved from: it survives a host swap, which
   * drops all in-memory state in this frame.
   *
   * Empty before the first successful poll, and after a dismiss.
   */
  taps: ShoulderTapItem[];
  /**
   * True once any tap in the active lifecycle has been accepted. Contact-
   * scoped, not per-tap.
   */
  accepted: boolean;
  isLoading: boolean;
  error: Error | null;
  /**
   * Tri-state health signal:
   *   - null   → not yet determined (before the first config/poll response)
   *   - false  → healthy (last observed request succeeded)
   *   - true   → offline (config fetch failed, or ≥ threshold consecutive poll failures)
   *
   * Consumers forwarding this to the host MUST skip the null state so the
   * host isn't told the service is healthy before we've verified it.
   */
  isOffline: boolean | null;
}

/**
 * Reactive shoulder-tap state for a given contactId.
 *
 * Fetches /shoulder-tap-configuration on mount (or when contactId
 * changes) to learn cadence and eligibility, then polls
 * /shoulder-tap-analysis on that cadence and exposes the latest tap.
 * Fail-closed: if the config request fails or returns enabled=false,
 * no polling starts and `tap` stays null.
 *
 * Repeated identical taps (same reason + message) don't produce a new
 * `tap` reference, so effects that depend on `tap` fire on real
 * transitions, not on every poll. `taps` (the whole active sequence, which
 * every poll returns) is deduped the same way.
 *
 * `isOffline` flips true when the config fetch fails or after
 * OFFLINE_FAILURE_THRESHOLD consecutive poll failures, and flips back
 * to false on the next successful poll. Consumers can forward this to
 * the host so it can surface a degraded / unavailable indicator.
 */
export function useShoulderTapAnalysis(contactId?: string | null): ShoulderTapAnalysisState {
  const [tap, setTap] = useState<ShoulderTapPayload | null>(null);
  const [taps, setTaps] = useState<ShoulderTapItem[]>([]);
  const [accepted, setAccepted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(contactId));
  const [error, setError] = useState<Error | null>(null);
  const [isOffline, setIsOffline] = useState<boolean | null>(null);

  // `logEvent` reads a module-global WebSocket at call time and gets a fresh
  // identity every render, so we stash it in a ref and read it from inside
  // the effect. That keeps it out of the effect's dependency array — we must
  // not tear down and restart polling just because a metric helper re-created.
  const { logEvent } = useMetrics();
  const logEventRef = useRef(logEvent);
  logEventRef.current = logEvent;

  useEffect(() => {
    if (!contactId) {
      setTap(null);
      setTaps([]);
      setAccepted(false);
      setIsLoading(false);
      setError(null);
      setIsOffline(null);
      return;
    }

    // Cleared on contact change so a previous contact's sequence can't leak
    // into this one's copy resolution.
    setTap(null);
    setTaps([]);
    setAccepted(false);
    setIsLoading(true);
    setError(null);
    setIsOffline(null);

    let cancelled = false;
    let timerId: number | null = null;
    let retryTimerId: number | null = null;
    let watchdogId: number | null = null;
    let consecutiveFailures = 0;
    // Timestamp (ms) at which the most recent poll *started*. The watchdog
    // compares this against wall-clock time to decide the loop has stalled.
    // Stamped at start (not completion) so a request that never settles is
    // still detectable.
    let lastPollStartedAt = 0;
    // Monotonic token identifying the active poll chain. Bumping it
    // "orphans" any in-flight poll: when a wedged request finally resolves,
    // its generation no longer matches and it neither updates state nor
    // reschedules, so a watchdog restart can't spawn a second parallel loop.
    let pollGeneration = 0;
    // Counts every poll attempt for the lifetime of this effect. A gap in the
    // sequence observed server-side means polls were lost; the sequence simply
    // stopping means the loop (or the whole frame) died.
    let pollSequence = 0;
    // Wall-clock time the current poll was scheduled to start, used to measure
    // how late the timer actually fired.
    let nextPollDueAt = 0;
    // Resolved from config once polling starts. Hoisted to effect scope so the
    // heartbeat can report the cadence it is being measured against.
    let intervalMs = DEFAULT_POLL_INTERVAL_SECONDS * 1000;

    /**
     * Proof-of-life for the poll loop. Deliberately cheap and sampled: the
     * value is in spotting the moment heartbeats stop, not in every tick, and
     * this runs on a hidden iframe's main thread where wasted work directly
     * feeds browser throttling.
     *
     * The detail only goes to breadcrumbs — the metrics channel carries just an
     * event type, so server-side this shows up as a per-contact timeline whose
     * *last* entry bounds when the frame went quiet.
     */
    const emitHeartbeat = (sequence: number, driftMs: number) => {
      breadcrumb('shoulder-tap.poll', {
        sequence,
        driftMs,
        intervalMs,
        visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
        usingWorkerTimers: isUsingWorkerTimers(),
      });
      if (sequence === 1 || sequence % HEARTBEAT_METRIC_EVERY_N_POLLS === 0) {
        logEventRef.current(METRICS_EVENTS.SHOULDER_TAP_HEARTBEAT);
      }
    };

    const start = async (attempt: number = 0) => {
      const config = await api.getShoulderTapConfiguration(contactId);
      if (cancelled) return;

      if (!config) {
        setError(new Error('Failed to fetch shoulder-tap configuration'));
        setIsOffline(true);
        setIsLoading(false);
        return;
      }
      if (!config.enabled) {
        // The config endpoint responded successfully, so the service
        // is reachable — publish healthy even though we won't be
        // polling. Otherwise a prior "offline" observation could stay
        // latched on the host forever.
        setIsOffline(false);

        // enabled=false at mount often just means the per-contact
        // eligibility latch hasn't been written yet (Kinesis Lambda
        // writes it on the first RTCA segment, which may lag the UI
        // by several seconds). Retry on a fixed schedule; on the
        // final attempt still returning false, give up and leave the
        // service in "not polling" state.
        if (attempt < CONFIG_RETRY_DELAYS_MS.length) {
          retryTimerId = setWorkerTimeout(() => {
            if (cancelled) return;
            start(attempt + 1);
          }, CONFIG_RETRY_DELAYS_MS[attempt]);
          return;
        }
        setIsLoading(false);
        return;
      }

      const intervalSeconds =
        config.poll_interval_seconds > 0
          ? config.poll_interval_seconds
          : DEFAULT_POLL_INTERVAL_SECONDS;
      intervalMs = intervalSeconds * 1000;

      // Recursive setTimeout instead of setInterval: a slow analysis
      // request must not stack a second concurrent request behind it.
      //
      // `generation` scopes this chain: if the watchdog restarts polling it
      // bumps pollGeneration, so a stale in-flight call from the old chain
      // becomes a no-op instead of updating state or rescheduling itself.
      const schedulePoll = async (generation: number) => {
        if (cancelled || generation !== pollGeneration) return;
        const scheduledFor = nextPollDueAt;
        lastPollStartedAt = Date.now();
        pollSequence += 1;

        // How late the timer actually fired. Under main-thread timer
        // throttling this grows without the request itself getting slower,
        // which is what separates "browser held our timer" from "server or
        // network is slow" — the two look identical in a network log.
        const driftMs = scheduledFor > 0 ? lastPollStartedAt - scheduledFor : 0;
        emitHeartbeat(pollSequence, driftMs);

        try {
          const result = await api.getShoulderTapAnalysis(contactId);
          if (cancelled || generation !== pollGeneration) return;

          if (!result) {
            setError(new Error('Failed to fetch shoulder-tap analysis'));
            consecutiveFailures += 1;
            if (consecutiveFailures >= OFFLINE_FAILURE_THRESHOLD) {
              setIsOffline(true);
            }
          } else {
            consecutiveFailures = 0;
            setIsOffline(false);
            setError(null);
            setTap((prev) => {
              // Preserve reference identity when the payload hasn't changed
              // so consumer effects don't re-fire on every poll.
              if (
                prev &&
                result.tap &&
                prev.reason === result.tap.reason &&
                prev.message === result.tap.message
              ) {
                return prev;
              }
              return result.tap;
            });
            // Same reason as the `tap` dedupe above, but this list arrives on
            // every poll: a fresh array each time would re-render the consumer
            // on every tick forever.
            const nextTaps = tapsFrom(result);
            setTaps((prev) => (sameTaps(prev, nextTaps) ? prev : nextTaps));
            setAccepted(Boolean(result.accepted));
          }
          setIsLoading(false);
        } catch (err) {
          // The API layer converts request failures to `null`, so a throw
          // here is an unexpected anomaly (not a normal offline signal). Log
          // it, but don't let it escape as an unhandled rejection or break
          // the loop — the finally below keeps polling regardless.
          if (!cancelled && generation === pollGeneration) {
            console.error('[shoulder-tap] unexpected error during poll:', err);
          }
        } finally {
          // Guaranteed reschedule: even if the body above threw, the loop
          // must keep ticking. Only the current, non-cancelled generation
          // is allowed to schedule the next poll.
          if (!cancelled && generation === pollGeneration) {
            nextPollDueAt = Date.now() + intervalMs;
            timerId = setWorkerTimeout(() => schedulePoll(generation), intervalMs);
          }
        }
      };

      // Watchdog: if no poll has *started* within the stall threshold, assume
      // the loop wedged (unexpected throw, suspended timer, an await that
      // never settled) and force-restart it. Bumping pollGeneration orphans
      // the wedged chain so we never end up double-polling.
      const stallThresholdMs = intervalMs * WATCHDOG_INTERVAL_MULTIPLIER;
      watchdogId = setWorkerInterval(() => {
        if (cancelled) return;
        const sinceLastPoll = Date.now() - lastPollStartedAt;
        if (sinceLastPoll <= stallThresholdMs) return;

        console.warn(
          `[shoulder-tap] poll loop stalled (${sinceLastPoll}ms since last poll start); restarting`,
        );
        // Best-effort: rides the chat metric WebSocket, which may be down at
        // the moment of a stall, so treat the count as a lower bound.
        logEventRef.current(METRICS_EVENTS.SHOULDER_TAP_WATCHDOG_RESTART);
        if (timerId) clearWorkerTimeout(timerId);
        pollGeneration += 1;
        schedulePoll(pollGeneration);
      }, intervalMs);

      schedulePoll(pollGeneration);
    };

    start();

    return () => {
      // Recorded so a silent stop can be attributed. If polling ends and this
      // breadcrumb is present, something unmounted us or changed contactId. If
      // polling ends with no teardown and no pagehide, the frame itself died.
      breadcrumb('shoulder-tap.teardown', { pollSequence, contactId });
      logEventRef.current(METRICS_EVENTS.SHOULDER_TAP_POLL_TEARDOWN);
      cancelled = true;
      if (timerId) clearWorkerTimeout(timerId);
      if (retryTimerId) clearWorkerTimeout(retryTimerId);
      if (watchdogId) clearWorkerInterval(watchdogId);
    };
  }, [contactId]);

  return { tap, taps, accepted, isLoading, error, isOffline };
}
